import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { LayoutDashboard, Table2, Upload, Users, ShieldCheck, Bell, LogOut, Menu, CheckCheck, Building2, HardHat } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/cases", label: "Cases", icon: Table2, testid: "nav-cases" },
  { to: "/clients", label: "Clients", icon: Building2, action: "manage_clients", testid: "nav-clients" },
  { to: "/field-executives", label: "Field Executives", icon: HardHat, action: "manage_fes", testid: "nav-field-executives" },
  { to: "/import", label: "Excel Import", icon: Upload, action: "import_excel", testid: "nav-import" },
  { to: "/users", label: "Users", icon: Users, action: "manage_users", testid: "nav-users" },
  { to: "/permissions", label: "Permissions", icon: ShieldCheck, action: "manage_permissions", testid: "nav-permissions" },
];

function NavItems({ onNavigate }) {
  const { can } = useAuth();
  const visibleNav = useMemo(() => NAV.filter((n) => !n.action || can(n.action)), [can]);
  return (
    <nav className="flex flex-col gap-1">
      {visibleNav.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate} data-testid={n.testid}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-sm transition-colors duration-150 ${
              isActive ? "bg-[#111827] text-white" : "text-[#4B5563] hover:bg-[#F3F4F6] hover:text-[#111827]"}`}>
          <n.icon className="h-4 w-4" />
          {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

function NotificationsBell() {
  const [items, setItems] = useState([]);
  const unread = items.filter((n) => !n.read).length;
  const load = () => api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- poll on mount only
  const readAll = async () => { await api.post("/notifications/read-all").catch(() => {}); load(); };
  return (
    <DropdownMenu onOpenChange={(o) => o && load()}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative rounded-sm" data-testid="notifications-bell-button">
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span data-testid="notifications-unread-count"
              className="absolute -top-1 -right-1 bg-[#EF4444] text-white text-[10px] font-bold px-1 min-w-[16px] h-4 flex items-center justify-center rounded-sm">
              {unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 rounded-sm">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {unread > 0 && (
            <button onClick={readAll} data-testid="notifications-mark-all-read"
              className="text-xs text-[#2563EB] flex items-center gap-1 hover:underline">
              <CheckCheck className="h-3 w-3" /> Mark all read
            </button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 && <div className="p-4 text-sm text-[#4B5563]" data-testid="notifications-empty">No notifications yet.</div>}
          {items.map((n) => (
            <DropdownMenuItem key={n.id} className="flex flex-col items-start gap-1 p-3 cursor-default" data-testid={`notification-item-${n.id}`}>
              <div className="flex items-center gap-2 w-full">
                {!n.read && <span className="h-2 w-2 rounded-full bg-[#2563EB] shrink-0" />}
                <span className="text-sm font-semibold text-[#111827]">{n.title}</span>
              </div>
              <span className="text-xs text-[#4B5563] leading-snug">{n.message}</span>
              <span className="text-[10px] text-[#9CA3AF]">{n.created_at ? new Date(n.created_at).toLocaleString() : ""}</span>
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    api.post("/notifications/scan").catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- api is a stable import

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <aside className="hidden md:flex fixed inset-y-0 left-0 w-56 flex-col border-r border-[#E5E7EB] bg-white z-30">
        <div className="px-4 py-5 border-b border-[#E5E7EB]">
          <div className="font-heading font-extrabold text-lg tracking-tight text-[#111827]" data-testid="app-logo">AccuSearch<span className="text-[#2563EB]"> WPM</span></div>
          <div className="text-[10px] uppercase tracking-widest text-[#9CA3AF] mt-0.5">Address Verification</div>
        </div>
        <div className="p-3 flex-1"><NavItems /></div>
        <div className="p-3 border-t border-[#E5E7EB] text-xs text-[#9CA3AF]">v1.0 Ops Console</div>
      </aside>

      <div className="md:pl-56 flex flex-col min-h-screen">
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b border-[#E5E7EB]">
          <div className="flex items-center gap-3 px-4 h-14">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden rounded-sm" data-testid="mobile-menu-button">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-4" aria-describedby={undefined}>
                <SheetHeader className="mb-4">
                  <SheetTitle className="font-heading font-extrabold text-lg text-left">AccuSearch<span className="text-[#2563EB]"> WPM</span></SheetTitle>
                </SheetHeader>
                <NavItems onNavigate={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>
            <div className="md:hidden font-heading font-extrabold text-base">AccuSearch<span className="text-[#2563EB]"> WPM</span></div>
            <div className="flex-1" />
            <NotificationsBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 hover:bg-[#F3F4F6] px-2 py-1.5 rounded-sm transition-colors" data-testid="user-menu-button">
                  <span className="h-7 w-7 bg-[#111827] text-white text-xs font-bold flex items-center justify-center rounded-sm">
                    {(user?.name || "U").slice(0, 1).toUpperCase()}
                  </span>
                  <span className="hidden sm:block text-left">
                    <span className="block text-xs font-semibold text-[#111827] leading-tight">{user?.name}</span>
                    <span className="block text-[10px] text-[#9CA3AF] leading-tight" data-testid="user-role-label">{user?.role_label}</span>
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-sm">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-[#EF4444]" data-testid="logout-button">
                  <LogOut className="h-4 w-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
